import { TrendingDown, TrendingUp } from "lucide-react"

import { DIMENSIONS, MAX_SCORE } from "@/lib/rubric"
import type { InterviewTurn, RubricScore } from "@/lib/types"

/**
 * The end-of-interview report: how the whole session went, at a glance.
 *
 * Drawn from the scores the rubric gave each answer – no model call, nothing
 * generated. That is deliberate: an AI-made picture of the result would be
 * prettier and could draw a number that is not the one you got. Every value
 * here is printed as a number as well, so nothing depends on reading a
 * colour.
 *
 * One measure (the 0–5 score), one hue: a bar per dimension for the session
 * average, then a grid with every answer's scores, shaded by value.
 */
export function SessionReport({ turns }: { turns: InterviewTurn[] }) {
  if (turns.length === 0) return null

  const averages = DIMENSIONS.map((dimension) => ({
    ...dimension,
    value: mean(turns.map((turn) => Number(turn.score[dimension.key]))),
  }))
  const overall = mean(turns.map((turn) => turn.score.overall))
  const strongest = averages.reduce((best, next) => (next.value > best.value ? next : best))
  const weakest = averages.reduce((worst, next) => (next.value < worst.value ? next : worst))
  const flat = strongest.value - weakest.value < 0.25

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-end gap-x-6 gap-y-2">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            Session average
          </p>
          <p className="tabular text-3xl font-semibold">
            {overall.toFixed(1)}
            <span className="text-base font-normal text-muted-foreground"> / {MAX_SCORE}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            over {turns.length} answer{turns.length === 1 ? "" : "s"}
          </p>
        </div>
        {!flat ? (
          <div className="space-y-1 text-sm">
            <p className="flex items-center gap-1.5">
              <TrendingUp className="size-4 text-success" aria-hidden />
              Strongest: <strong>{strongest.label}</strong> ({strongest.value.toFixed(1)})
            </p>
            <p className="flex items-center gap-1.5">
              <TrendingDown className="size-4 text-warning" aria-hidden />
              Work on: <strong>{weakest.label}</strong> ({weakest.value.toFixed(1)}) –{" "}
              <span className="text-muted-foreground">{weakest.hint.toLowerCase()}</span>
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Even across all five dimensions.</p>
        )}
      </div>

      <figure className="space-y-2">
        <figcaption className="text-xs font-medium text-muted-foreground">
          Average per dimension (0–{MAX_SCORE})
        </figcaption>
        <div className="space-y-2" role="list">
          {averages.map((dimension) => (
            <div
              key={dimension.key}
              role="listitem"
              className="group grid grid-cols-[96px_minmax(0,1fr)_36px] items-center gap-3"
              title={`${dimension.label}: ${dimension.value.toFixed(1)} of ${MAX_SCORE}, average of ${turns.length} answer${turns.length === 1 ? "" : "s"}`}
            >
              <span className="text-sm">{dimension.label}</span>
              <div className="relative h-3 rounded-full bg-muted">
                {[1, 2, 3, 4].map((tick) => (
                  <span
                    key={tick}
                    aria-hidden
                    className="absolute inset-y-0 w-px bg-background/70"
                    style={{ left: `${(tick / MAX_SCORE) * 100}%` }}
                  />
                ))}
                <div
                  className="h-full rounded-full bg-primary transition-opacity group-hover:opacity-80"
                  style={{ width: `${Math.max((dimension.value / MAX_SCORE) * 100, 2)}%` }}
                />
              </div>
              <span className="tabular text-right text-sm">{dimension.value.toFixed(1)}</span>
            </div>
          ))}
        </div>
      </figure>

      <figure className="space-y-2">
        <figcaption className="text-xs font-medium text-muted-foreground">
          Every answer – darker is higher
        </figcaption>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-separate border-spacing-[2px] text-sm">
            <thead className="text-xs text-muted-foreground">
              <tr>
                <th className="px-2 py-1 text-left font-medium">Answer</th>
                {DIMENSIONS.map((dimension) => (
                  <th key={dimension.key} className="px-2 py-1 font-medium">
                    {dimension.label}
                  </th>
                ))}
                <th className="px-2 py-1 font-medium">Overall</th>
              </tr>
            </thead>
            <tbody>
              {turns.map((turn, index) => (
                <tr key={`${index}-${turn.question}`}>
                  <th
                    scope="row"
                    className="max-w-[220px] truncate px-2 py-1 text-left font-normal"
                    title={turn.question}
                  >
                    {turn.isProbe ? "Follow-up" : `Q${questionNumber(turns, index)}`}
                    <span className="ml-1.5 text-xs text-muted-foreground">{turn.question}</span>
                  </th>
                  {[...DIMENSIONS.map((d) => d.key), "overall" as keyof RubricScore].map((key) => (
                    <ScoreCell key={key} value={Number(turn.score[key])} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </figure>
    </div>
  )
}

function ScoreCell({ value }: { value: number }) {
  const share = Math.max(0, Math.min(1, value / MAX_SCORE))
  // One hue, light to dark; the number is always printed, so the shade is
  // a help to the eye, never the only way to read the value.
  return (
    <td
      className="tabular rounded px-2 py-1 text-center"
      style={{ backgroundColor: `hsl(var(--primary) / ${0.08 + share * 0.42})` }}
    >
      {value.toFixed(1)}
    </td>
  )
}

function mean(values: number[]): number {
  return values.length ? values.reduce((sum, value) => sum + value, 0) / values.length : 0
}

/** Q1, Q2 … counting main questions only; follow-ups belong to the one before. */
function questionNumber(turns: InterviewTurn[], index: number): number {
  return turns.slice(0, index + 1).filter((turn) => !turn.isProbe).length
}
